import _ from 'lodash';

import * as React from 'react';

import * as api from '../api';
import * as logger from '../logger';
import * as store from '../reducers/store';

import { Icon } from '@vivaahealth/design-system';

import { constructAddress } from './practice';
import { isTruthy } from '../components/utils';
import { maybeFormatNumberToNational } from './phoneNumber';
import { isEnumMember } from 'common/dist/utils/types';
import { parseFieldValue } from '../scenes/Form/InsurancePage/v2/utils';
import { PersonalInformationHeader } from '../scenes/Review/Header';

const { Primitive } = api.PrevisitFormTemplateFieldConfig;

export const HIDDEN_REVIEWABLE_PRIMITIVES: api.PrevisitFormTemplateFieldConfig.Primitive[] =
  [Primitive.PatientFirstName, Primitive.PatientLastName];

export enum PersonalInformationSectionType {
  PersonalInfo = 'PERSONAL_INFORMATION',
  EmergencyContact = 'EMERGENCY_CONTACT',
  EmergencyContacts = 'EMERGENCY_CONTACTS',
  Demographics = 'DEMOGRAPHICS',
  InsuranceCards = 'INSURANCE_CARDS',
  Guarantor = 'GUARANTOR',
  Identification = 'IDENTIFICATION',
  AdditionalInformation = 'ADDITIONAL_INFORMATION',
}

export enum ClinicalReviewSectionType {
  Pharmacy = 'PHARMACY',
  Allergies = 'ALLERGIES',
  CareProviders = 'CARE_PROVIDERS',
  PersonalMedicalHistory = 'PERSONAL_MEDICAL_HISTORY',
  FamilyMedicalHistory = 'FAMILY_MEDICAL_HISTORY',
}

export type SectionType =
  | PersonalInformationSectionType
  | ClinicalReviewSectionType;

export enum ReviewDataType {
  Primitive = 'PRIMITIVE',
  DataBinding = 'DATA_BINDING',
}

export type ReviewData =
  | {
      type: ReviewDataType.Primitive;
      value: api.PrevisitFormTemplateFieldConfig.Primitive;
    }
  | {
      type: ReviewDataType.DataBinding;
      value: string;
    };

export interface GroupedData<DATA> {
  /**
   * The primitives to display
   */
  data: DATA[];

  /**
   * The icon associated with the primitive grouping
   */
  Icon?: React.ComponentType<any>;

  /**
   * The number of columns to use when displaying the given primitives
   * @default 1
   */
  columns?: number;

  /**
   * If true, don't render a row for this group.
   * @default false
   */
  hideRow?: boolean;
}

export interface ReviewSection {
  Header?: ConfigurationHeader;
  body: GroupedData<ReviewData>[];
}

export type GroupedFields = Omit<GroupedData<ReviewData>, 'data'> & {
  fields: store.FormFieldWithConfig[];
};

export type ConfigurationHeader = ({
  fields,
}: {
  fields: store.FormFieldWithConfig[];
}) => React.JSX.Element | null;

export const PATIENT_INFORMATION_SECTIONS: Record<
  PersonalInformationSectionType,
  ReviewSection
> = {
  [PersonalInformationSectionType.PersonalInfo]: {
    body: [
      {
        data: [
          { type: ReviewDataType.Primitive, value: Primitive.PatientFirstName },
          { type: ReviewDataType.Primitive, value: Primitive.PatientLastName },
        ],
        columns: 2,
        Icon: Icon.User,
      },
      {
        data: [
          { type: ReviewDataType.Primitive, value: Primitive.PatientDob },
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientPreferredFirstName,
          },
        ],
        columns: 2,
        Icon: Icon.User,
      },
      {
        data: [
          { type: ReviewDataType.Primitive, value: Primitive.PatientCellPhone },
          { type: ReviewDataType.Primitive, value: Primitive.PatientHomePhone },
          { type: ReviewDataType.Primitive, value: Primitive.PatientWorkPhone },
          {
            type: ReviewDataType.Primitive,
            value: Primitive.Phone,
          },
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientPreferredPhone,
          },
        ],
        Icon: Icon.Phone,
        columns: 2,
      },
      {
        data: [
          { type: ReviewDataType.Primitive, value: Primitive.PatientEmail },
        ],
        Icon: Icon.Mail,
      },
      {
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientAddressPhysical,
          },
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientAddressMailing,
          },
        ],
        Icon: Icon.MapPin,
      },
      {
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientPreferredLanguage,
          },
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientWrittenLanguages,
          },
        ],
        Icon: Icon.Globe,
        columns: 2,
      },
      {
        data: [
          { type: ReviewDataType.DataBinding, value: 'patients.dependents' },
        ],
        Icon: Icon.Users,
      },
    ],
    Header: PersonalInformationHeader,
  },
  [PersonalInformationSectionType.EmergencyContact]: {
    body: [
      {
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientEmergencyContact,
          },
        ],
        hideRow: true,
      },
      {
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientRelativeEmergencyContact,
          },
        ],
        hideRow: true,
      },
    ],
  },
  [PersonalInformationSectionType.EmergencyContacts]: {
    body: [
      {
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientEmergencyContacts,
          },
        ],
        hideRow: true,
      },
    ],
  },
  [PersonalInformationSectionType.Demographics]: {
    body: [
      {
        data: [{ type: ReviewDataType.Primitive, value: Primitive.PatientSex }],
        Icon: Icon.User,
      },
      {
        data: [
          { type: ReviewDataType.Primitive, value: Primitive.PatientRace },
          { type: ReviewDataType.Primitive, value: Primitive.PatientEthnicity },
        ],
        Icon: Icon.Globe,
      },
      {
        data: [
          { type: ReviewDataType.Primitive, value: Primitive.PatientReligion },
        ],
        Icon: Icon.BookOpen,
      },
      {
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientMaritalStatus,
          },
        ],
        Icon: Icon.Users,
      },
      {
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientEmploymentStatus,
          },
          { type: ReviewDataType.Primitive, value: Primitive.PatientEmployer },
          { type: ReviewDataType.DataBinding, value: 'patients.occupation' },
          { type: ReviewDataType.DataBinding, value: 'patients.employerPhone' },
        ],
        Icon: Icon.Briefcase,
        columns: 2,
      },
      {
        data: [
          { type: ReviewDataType.DataBinding, value: 'patients.veteranStatus' },
        ],
        Icon: Icon.Flag,
      },
    ],
  },
  [PersonalInformationSectionType.InsuranceCards]: {
    body: [
      {
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientInsuranceCardPhotoUpload,
          },
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientInsuranceCardUpload,
          },
          { type: ReviewDataType.Primitive, value: Primitive.PatientInsurance },
        ],
        columns: 2,
        hideRow: true,
      },
    ],
  },
  [PersonalInformationSectionType.Guarantor]: {
    body: [
      {
        Icon: Icon.User,
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientGuarantors,
          },
        ],
        hideRow: true,
      },
    ],
  },
  [PersonalInformationSectionType.Identification]: {
    body: [
      {
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientPhotoIdUpload,
          },
        ],
        hideRow: true,
      },
    ],
  },
  [PersonalInformationSectionType.AdditionalInformation]: {
    body: [
      {
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.TextInput,
          },
        ],
      },
    ],
  },
};

export const CLINICAL_INFORMATION_SECTIONS: Record<
  ClinicalReviewSectionType,
  ReviewSection
> = {
  [ClinicalReviewSectionType.Pharmacy]: {
    body: [
      {
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientPharmacies,
          },
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientNCPDPPharmacies,
          },
        ],
        hideRow: true,
      },
    ],
  },
  [ClinicalReviewSectionType.Allergies]: {
    body: [
      {
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientAllergies,
          },
        ],
        Icon: Icon.Activity,
      },
    ],
  },
  [ClinicalReviewSectionType.CareProviders]: {
    body: [
      {
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientCareProviders,
          },
        ],
        hideRow: true,
      },
    ],
  },
  [ClinicalReviewSectionType.PersonalMedicalHistory]: {
    body: [
      {
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientMedicalHistoryPersonal,
          },
        ],
        Icon: Icon.User,
      },
      {
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientSurgicalHistory,
          },
        ],
        Icon: Icon.Activity,
      },
    ],
  },
  [ClinicalReviewSectionType.FamilyMedicalHistory]: {
    body: [
      {
        data: [
          {
            type: ReviewDataType.Primitive,
            value: Primitive.PatientMedicalHistoryFamily,
          },
        ],
        Icon: Icon.Users,
      },
    ],
  },
};

export const REVIEW_SECTIONS: Record<SectionType, ReviewSection> = {
  ...PATIENT_INFORMATION_SECTIONS,
  ...CLINICAL_INFORMATION_SECTIONS,
};

export const getFieldsBySection = (
  fields: store.FormFieldWithConfig[],
  section: SectionType,
  configuration: Record<SectionType, ReviewSection>,
) => {
  const reviewSection = configuration[section];
  const data = reviewSection.body.flatMap((groupedData) => groupedData.data);
  return fields.filter((field) =>
    data.some((d) =>
      d.type === ReviewDataType.Primitive
        ? field.primitive === d.value
        : field.primitive === Primitive.Enum && field.dataBinding === d.value,
    ),
  );
};

export const getParsedValue = ({
  value,
  primitive,
}: Partial<store.FormFieldWithConfig>): string => {
  if (!value) return '';
  switch (primitive) {
    case Primitive.PatientSex:
      return api.Previsit.sexOptionsMap[value as api.Previsit.Sex];

    case Primitive.PatientEmployer:
      const { employerRaw }: api.Employer.EmployerFormFieldValue =
        JSON.parse(value);
      return employerRaw;

    case Primitive.PatientRace:
      const races: api.Patient.Race[] = JSON.parse(value);
      const displayValues = races.map((race) => api.Previsit.raceMap[race]);
      return displayValues.join(', ');

    case Primitive.PatientEthnicity:
      return api.Previsit.ethnicityOptionsMap[value as api.Previsit.Ethnicity];

    case Primitive.PatientMaritalStatus:
      return api.Previsit.maritalStatusOptionsMap[
        value as api.Previsit.MaritalStatus
      ];

    case Primitive.PatientAddressMailing:
    case Primitive.PatientAddressPhysical:
      const addressFull: api.Places.PlaceDetails = JSON.parse(value);
      return constructAddress({
        address: [addressFull.addressOne, addressFull.addressTwo]
          .filter(isTruthy)
          .join(', '),
        city: addressFull.city,
        state: addressFull.state,
        zip: addressFull.zipCode,
      });

    case Primitive.Phone:
    case Primitive.PatientCellPhone:
    case Primitive.PatientHomePhone:
    case Primitive.PatientPhone:
    case Primitive.PatientWorkPhone:
      return maybeFormatNumberToNational(value) || '';

    case Primitive.PatientPreferredPhone:
      return api.Previsit.phoneNumberKindMap[
        value as api.Common.PhoneNumberKind
      ];

    case Primitive.PatientEmploymentStatus:
      return api.Previsit.employmentStatusOptionsMap[
        value as api.Previsit.EmploymentStatus
      ];

    case Primitive.PatientReligion:
      if (!isEnumMember(api.Patient.Religion, value)) return '';
      const displayReligion = api.Previsit.religionMap[value];
      return displayReligion;

    case Primitive.PatientWrittenLanguages:
      const writtenLanguages: api.Language.LanguageWithOther[] =
        JSON.parse(value);
      const displayWrittenLanguages = writtenLanguages.map(
        (lang) => api.Language.languageMap[lang],
      );
      return displayWrittenLanguages.join(', ');

    case Primitive.PatientPreferredLanguage:
      if (!isEnumMember(api.Language.LanguageWithOther, value)) return '';
      return api.Language.languageMap[value];
    default:
      return value;
  }
};

export const getLatestGuarantor = (
  guarantorFieldValue: api.Guarantor.PrevisitGuarantorFormFieldValue,
) => {
  const { guarantors } = guarantorFieldValue;
  const latestGuarantor = _.maxBy(guarantors, (guarantor) =>
    new Date(guarantor.updated).getTime(),
  );
  return latestGuarantor;
};

/**
 * The insurance card primitive and whether user will be using the insurance paying for the visit should really be
 * two separate questions. The cards should be on review screen, but whether paying insurance for the specific visit
 * should be on a per encounter form. Since the insurance field is not considered as `completed` unless the `paying
 * with insurance` is answered, we will need to populate it to be true. Similar things for other relevant fields to
 * make sure export works correctly.
 * Similar for guarantor primitive. The pre-populated value will not pass our `completed` check. So we will assume latest updated is seleceted.
 * @deprecated hack do not use
 */

export const maybeForceCompleteInsuranceRelevantFields = async ({
  fields,
  setFieldValue,
  saveFieldValue,
}: {
  fields: store.FormFieldWithConfig[];
  setFieldValue: (formFieldId: string, value: string) => void;
  saveFieldValue: (formFieldId: string, value: string) => Promise<void>;
}) => {
  let insuranceFields: store.FormFieldWithConfig[] = [];
  let guarantorFields: store.FormFieldWithConfig[] = [];
  let otherFields: store.FormFieldWithConfig[] = [];
  for (const field of fields) {
    if (
      [
        Primitive.PatientInsuranceCardUpload,
        Primitive.PatientInsuranceCardPhotoUpload,
        Primitive.PatientInsurance,
      ].includes(field.primitive)
    ) {
      insuranceFields.push(field);
    } else if (field.primitive === Primitive.PatientGuarantors) {
      guarantorFields.push(field);
    } else {
      otherFields.push(field);
    }
  }
  const updatesInsuranceRequests = insuranceFields.map((field) => {
    return forceCompleteInsuranceField({
      field,
      setFieldValue,
      saveFieldValue,
    });
  });
  const updatesGuarantorRequests = guarantorFields.map((field) => {
    return forceCompleteGuarantorField({
      field,
      setFieldValue,
      saveFieldValue,
    });
  });
  let updatedFields: store.FormFieldWithConfig[] = [];
  try {
    updatedFields = await Promise.all([
      ...updatesInsuranceRequests,
      ...updatesGuarantorRequests,
    ]);
  } catch (error) {
    logger.error(error, 'Unable to force update insurance relevant primitives');
    throw error;
  }
  return [...otherFields, ...updatedFields];
};

// --------------------------------- Private ---------------------------------
const forceCompleteInsuranceField = async ({
  field,
  setFieldValue,
  saveFieldValue,
}: {
  field: store.FormFieldWithConfig;
  setFieldValue: (formFieldId: string, value: string) => void;
  saveFieldValue: (formFieldId: string, value: string) => Promise<void>;
}): Promise<store.FormFieldWithConfig> => {
  const parsedValue = parseFieldValue(field.value, false);
  if (!parsedValue) {
    logger.error(
      new Error(
        `Unable to parse insurance field ${field.id} value ${field.value} during review process.`,
      ),
    );
    return field;
  }
  if (
    parsedValue.payingWithInsurance !== undefined &&
    parsedValue.payingWithInsurance !== null
  ) {
    return field;
  }
  const updatedValue = JSON.stringify(
    transformInsuranceValueToConfirmed(parsedValue),
  );
  await saveFieldValue(field.id, updatedValue);
  setFieldValue(field.id, updatedValue);
  return { ...field, value: updatedValue };
};

const forceCompleteGuarantorField = async ({
  field,
  setFieldValue,
  saveFieldValue,
}: {
  field: store.FormFieldWithConfig;
  setFieldValue: (formFieldId: string, value: string) => void;
  saveFieldValue: (formFieldId: string, value: string) => Promise<void>;
}): Promise<store.FormFieldWithConfig> => {
  const parsedGuarantorFieldValue: api.Guarantor.PrevisitGuarantorFormFieldValue =
    JSON.parse(field.value);
  if (parsedGuarantorFieldValue.selectedGuarantorId) return field;
  const latestGuarantor = getLatestGuarantor(parsedGuarantorFieldValue);
  const updatedValue = JSON.stringify({
    ...parsedGuarantorFieldValue,
    selectedGuarantorId: latestGuarantor?.id,
    isSomeoneElse:
      latestGuarantor?.relationshipToPatient &&
      latestGuarantor?.relationshipToPatient !== api.Common.Relationship.Self,
  });
  await saveFieldValue(field.id, updatedValue);
  setFieldValue(field.id, updatedValue);
  return { ...field, value: updatedValue };
};

const transformInsuranceValueToConfirmed = (
  value: api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue,
) => {
  const insuranceCoverages = value.extractedPatientInsuranceCoverages?.map(
    (extracted) => ({
      id: extracted.id,
      payor: extracted.payor,
      primary: extracted.primary,
      primaryPolicyHolderMemberId: extracted.primaryPolicyHolderMemberId,
      groupNumber: extracted.groupNumber,
      coverageStartDate: extracted.coverageStartDate,
      subscriberId: extracted.id,
    }),
  );
  const confirmedInsuranceIds = insuranceCoverages?.map(
    (extractedCoverage) => extractedCoverage.id,
  );
  const updated = {
    ...value,
    payingWithInsurance: true,
    confirmedInsuranceIds: confirmedInsuranceIds,
    insuranceCoverages: insuranceCoverages,
    footprint: api.PrevisitPatientInsurance.Footprint.ReviewDirectSubmit,
  };
  return updated;
};
